// @noflow
import { find, forEach, isArray, isEmpty, values, groupBy, toPairs } from 'lodash';
import type { Action, State, Franchise, FranchiseMap } from './types';
import DvrEventListing from '../models/DvrEventListing';

const initialState = {
  franchiseSlug: null,
  linkContent: false,
  showContent: false,
  recommendations: {
    fetching: false,
    content: [],
    error: false,
  },
  franchiseDetails: {
    fetching: false,
    error: false,
    requireRefresh: false,
    bySlug: {},
  },
  recentlyWatchedContentId: null,
  recentlyWatchedProgress: {},
  fetchingFullRequest: false,
};

const receiveFranchise = (slug: string, bySlug: FranchiseMap, payload: Franchise) => {
  const franchiseMap = {
    ...bySlug,
  };

  if (!isEmpty(payload)) {
    // it is important to use a slug used for a server request here
    // do not replace it with a slug from a franchise object
    franchiseMap[slug] = { ...payload, full: true, previewable: true };
  }
  return franchiseMap;
};

const receiveFranchisesArray = (bySlug: FranchiseMap, payload: Array<Object>) => {
  const franchiseMap = {
    ...bySlug,
  };

  if (isArray(payload)) {
    forEach(payload, franchiseItem => {
      if (franchiseItem.slug && !franchiseMap[franchiseItem.slug]) {
        franchiseMap[franchiseItem.slug] = franchiseItem;
      }
    });
  }
  return franchiseMap;
};

const removeRecordingFromFranchise = (
  state: State,
  recordingId: string,
  eventListing: ?DvrEventListing,
) => {
  if (eventListing) {
    const { seriesId } = eventListing;
    const { bySlug } = state.franchiseDetails;
    // searching for franchises by seriesId because sometimes the same franchise
    // can have different slugs in recordings and everywhere else
    values(bySlug).forEach(franchise => {
      if (
        franchise.content_identification &&
        franchise.content_identification.series_id === seriesId
      ) {
        franchise.recordings = franchise.recordings.filter(item => item.id !== recordingId);
      }
    });
  }
};

const receiveRecordings = (payload, bySlug) => {
  const franchiseMap = {
    ...bySlug,
  };

  if (isArray(payload) && !isEmpty(payload)) {
    const grouped = groupBy(payload.filter(item => item.franchise != null), item => item.series_id);
    toPairs(grouped).forEach(([seriesId, franchiseRecordings]) => {
      const { franchise } = franchiseRecordings[0];
      const { slug } = franchise;
      if (slug != null && seriesId != null) {
        // remove franchise from recordings
        const recordings = franchiseRecordings.map(item => ({ ...item, franchise: undefined }));
        const oldFranchise = franchiseMap[slug];
        franchiseMap[slug] = {
          ...oldFranchise,
          ...franchise,
          recordings,
          previewable: true,
        };
      }
    });
  }

  return franchiseMap;
};

const receiveRecents = (payload, bySlug) => {
  const franchiseMap = {
    ...bySlug,
  };

  if (payload && payload.upid && payload.total_seconds) {
    forEach(franchiseMap, franchise => {
      const recording = find(franchise.recordings, ['upid', payload.upid]);

      if (recording) {
        recording.progress_seconds = payload.progress_seconds;
        recording.progress_percent = Math.floor(
          (payload.progress_seconds / payload.total_seconds) * 100,
        );
        recording.total_seconds = payload.total_seconds;
      }
    });
  }

  return franchiseMap;
};

const reducer = (state: State = initialState, action: Action): State => {
  switch (action.type) {
    case 'REQUEST_FRANCHISE':
      return {
        ...state,
        franchiseSlug: action.slug,
        recommendations: action.requestRecommendations
          ? initialState.recommendations
          : state.recommendations,
        franchiseDetails: {
          ...state.franchiseDetails,
          fetching: !action.isFullRequest,
          error: false,
        },
        fetchingFullRequest: action.isFullRequest,
      };
    case 'RECEIVE_FRANCHISE':
      return {
        ...state,
        franchiseSlug: action.slug,
        franchiseDetails: {
          error: false,
          fetching: false,
          bySlug: receiveFranchise(action.slug, state.franchiseDetails.bySlug, action.payload),
        },
        fetchingFullRequest: false,
      };

    case 'SET_REQUIRE_FRANCHISE_REFRESH':
      return {
        ...state,
        franchiseDetails: {
          ...state.franchiseDetails,
          requireRefresh: true,
        },
      };
    case 'UNSET_REQUIRE_FRANCHISE_REFRESH':
      return {
        ...state,
        franchiseDetails: {
          ...state.franchiseDetails,
          requireRefresh: false,
        },
      };
    case 'REQUEST_FRANCHISE_ERROR':
      return {
        ...state,
        franchiseDetails: {
          ...state.franchiseDetails,
          error: true,
          fetching: false,
        },
        fetchingFullRequest: false,
      };
    case 'REQUEST_RECOMMENDATIONS':
      return {
        ...state,
        recommendations: {
          fetching: true,
          content: [],
          error: false,
        },
      };
    case 'RECEIVE_RECOMMENDATIONS': {
      return {
        ...state,
        recommendations: {
          fetching: false,
          error: false,
          content: action.payload,
        },
        franchiseDetails: {
          ...state.franchiseDetails,
          bySlug: receiveFranchisesArray(state.franchiseDetails.bySlug, action.payload),
        },
      };
    }
    case 'REQUEST_RECOMMENDATIONS_ERROR':
      return {
        ...state,
        recommendations: {
          fetching: false,
          error: true,
          content: [],
        },
      };
    case 'UPDATE_LINK_CONTENT':
      return {
        ...state,
        showContent: true,
        linkContent: action.payload.linkContent,
      };
    case 'SHOW_LINK_CONTENT':
      return {
        ...state,
        showContent: true,
      };
    case 'CLOSE_LINK_CONTENT':
      return {
        ...state,
        showContent: false,
      };
    case 'RECEIVE_DELETE_RECORDING': {
      removeRecordingFromFranchise(state, action.id, action.eventListing);
      return {
        ...state,
      };
    }
    case 'RESPONSE_HOME_PAGE_CONTENT': {
      return {
        ...state,
        franchiseDetails: {
          ...state.franchiseDetails,
          bySlug: receiveFranchisesArray(state.franchiseDetails.bySlug, action.payload),
        },
      };
    }
    case 'RECEIVE_PURCHASES': {
      return {
        ...state,
        franchiseDetails: {
          ...state.franchiseDetails,
          bySlug: receiveFranchisesArray(state.franchiseDetails.bySlug, action.payload?.purchases),
        },
      };
    }
    case 'SET_RECENTLY_WATCHED_CONTENT_ID': {
      return {
        ...state,
        recentlyWatchedContentId: action.contentId,
      };
    }
    case '@@found/RESOLVE_MATCH': {
      if (
        state.franchiseSlug &&
        !action.payload?.location?.pathname.includes(state.franchiseSlug)
      ) {
        return {
          ...state,
          recentlyWatchedContentId: null,
        };
      }
      return state;
    }
    case '10FT_RECEIVE_RECORDINGS': {
      const { payload } = action;

      return {
        ...state,
        franchiseDetails: {
          ...state.franchiseDetails,
          bySlug: receiveRecordings(payload, state.franchiseDetails.bySlug),
        },
      };
    }
    case 'SET_FRANCHISE_PROGRESS': {
      const { payload } = action;

      return {
        ...state,
        recentlyWatchedProgress: {
          ...state.recentlyWatchedProgress,
          ...payload,
        },
      };
    }
    case 'PUT_TO_RECENTS': {
      const { payload } = action;

      return {
        ...state,
        franchiseDetails: {
          ...state.franchiseDetails,
          bySlug: receiveRecents(payload, state.franchiseDetails.bySlug),
        },
      };
    }
    default:
      /* Following line assures switch exhaustiveness */
      (action.type: empty); /* eslint-disable-line */
      return state;
  }
};

export default reducer;
